import { Link } from 'react-router';
import { Badge, Card } from '@skupreme/shared/ui';
import { DateFormat, DateUtility } from '@utility/date';
import { formatOrderStatus } from '../lib/status';

export type OrderStatusHistoryItem = {
  id: string;
  catalogId?: string;
  title: string;
  sku?: string;
  date: string | Date;
};

export type OrderStatusHistoryEntry = {
  state: string;
  date?: string | Date | null;
  /** The lines that reached this state, each with its own date. */
  items?: OrderStatusHistoryItem[];
  /** Only some of the lines reached this state. */
  partial?: boolean;
  /** The order is past its ship-by date and has not finished delivering. */
  delayed?: boolean;
  deliveredCount?: number;
  totalDeliverableLabels?: number;
};

export type OrderStatusHistoryCardProps = {
  entries: OrderStatusHistoryEntry[];
};

type HistoryRow = {
  key: string;
  state: string;
  date: string | Date;
  entry: OrderStatusHistoryEntry;
  item?: OrderStatusHistoryItem;
  itemCount: number;
};

const time = (date: string | Date) => new Date(date).getTime();

/**
 * An order can move through a state one line at a time, and a single row per
 * state would report only the last of them. Lines that moved together stay
 * collapsed; lines that split get a row each.
 */
function toRows(entry: OrderStatusHistoryEntry): HistoryRow[] {
  const items = entry.items || [];
  const movedApart =
    items.length > 1 && new Set(items.map(item => time(item.date))).size > 1;

  if (!entry.date) {
    return [];
  }

  if (!movedApart) {
    return [
      {
        key: `${entry.state}-${entry.date.toString()}`,
        state: entry.state,
        date: entry.date,
        entry,
        itemCount: items.length,
      },
    ];
  }

  return items.map(item => ({
    key: `${entry.state}-${item.id}-${item.date.toString()}`,
    state: entry.state,
    date: item.date,
    entry,
    item,
    itemCount: 1,
  }));
}

function ItemLabel({ item }: { item: OrderStatusHistoryItem }) {
  const label = item.sku || item.title;

  if (!item.catalogId) {
    return (
      <span
        className="sk:min-w-0 sk:truncate sk:text-dense sk:text-fg-secondary"
        title={item.title}
      >
        {label}
      </span>
    );
  }

  return (
    <Link
      to={`/app/catalogs/catalog/${encodeURIComponent(item.catalogId)}`}
      className="sk:min-w-0 sk:truncate sk:text-dense sk:text-link sk:hover:text-link sk:hover:underline"
      title={item.title}
    >
      {label}
    </Link>
  );
}

function rowMeta(row: HistoryRow): string | null {
  const { deliveredCount, totalDeliverableLabels } = row.entry;

  if (deliveredCount && totalDeliverableLabels) {
    return `${deliveredCount} of ${totalDeliverableLabels} delivered`;
  }

  if (!row.item && row.itemCount > 1) {
    return `${row.itemCount} items`;
  }

  return null;
}

export function OrderStatusHistoryCard({
  entries,
}: OrderStatusHistoryCardProps) {
  const history = entries
    .flatMap(toRows)
    .sort((left, right) => time(left.date) - time(right.date));

  /* An order-level fact, not a state's: it holds until delivery completes, and
     the state it belongs to may have no date yet and so no row of its own. */
  const isDelayed = entries.some(entry => entry.delayed);

  return (
    <Card
      title="Status history"
      action={
        isDelayed ? (
          <Badge tone="danger" className="sk:text-xs sk:font-normal">
            Delayed
          </Badge>
        ) : undefined
      }
      bodyClassName="sk:p-0"
    >
      {history.length === 0 ? (
        <p className="sk:m-0 sk:p-4 sk:text-dense sk:text-fg-tertiary">
          No status history
        </p>
      ) : (
        <ol className="sk:m-0 sk:list-none sk:p-0">
          {history.map(row => {
            const meta = rowMeta(row);

            return (
              <li
                key={row.key}
                className="sk:grid sk:grid-cols-[10rem_minmax(0,1fr)_9rem] sk:items-baseline sk:gap-4 sk:border-b sk:border-rule sk:px-4 sk:py-3 sk:last:border-b-0"
              >
                <time className="sk:whitespace-nowrap sk:text-dense sk:text-fg-tertiary">
                  {DateUtility.formatDate(row.date, DateFormat.dateTime)}
                </time>
                <span className="sk:flex sk:min-w-0 sk:items-baseline sk:gap-2">
                  <span className="sk:relative sk:inline-block sk:shrink-0 sk:pl-4 sk:text-dense sk:font-normal sk:text-fg">
                    <span
                      aria-hidden="true"
                      className="sk:absolute sk:top-1/2 sk:left-0 sk:size-2.5 sk:-translate-y-1/2 sk:rounded-full sk:bg-success"
                    />
                    <span>{formatOrderStatus(row.state)}</span>
                  </span>
                  {row.item ? <ItemLabel item={row.item} /> : null}
                </span>
                <span className="sk:min-w-0 sk:truncate sk:text-xs sk:text-fg-secondary">
                  {row.entry.partial ? (
                    <Badge tone="caution" className="sk:text-xs sk:font-normal">
                      Partial
                    </Badge>
                  ) : (
                    meta
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
